// GEO-001: Nepal administrative geography (7 provinces, 77 districts, 753 local levels, 6,743 wards)
// as a static, typed module for the checkout cascade and the delivery-zone resolver. No D1 read is
// needed at checkout (DATA_MODEL §17). The control-plane `geo_*` tables are seeded from the same
// data/geo/seed CSVs.
//
// Distribution status: interim internal-use until the owner or counsel confirms the source licences
// (D-028, data/geo/SOURCES.md). Names are data, not UI strings (D-013), so both scripts are kept.
import {
  COURIER_AREA_ROWS,
  DATASET,
  DISTRICT_ROWS,
  LOCAL_LEVEL_ROWS,
  PROVINCE_ROWS,
  ZONE_ROWS,
} from "./data.generated";

export { DATASET };

export const LOCAL_LEVEL_TYPES = [
  "metropolitan",
  "sub_metropolitan",
  "municipality",
  "rural_municipality",
] as const;
export type LocalLevelType = (typeof LOCAL_LEVEL_TYPES)[number];

export type Province = {
  readonly code: string;
  readonly number: number;
  readonly nameEn: string;
  readonly nameNe: string;
  readonly hdxPcode: string;
};

export type District = {
  readonly code: string;
  readonly provinceCode: string;
  readonly nameEn: string;
  readonly nameNe: string;
  readonly hdxPcode: string;
  readonly aliases: readonly string[];
};

export type LocalLevel = {
  readonly code: string;
  readonly districtCode: string;
  readonly type: LocalLevelType;
  readonly nameEn: string;
  /** Empty when the MoFAGA directory does not list this local level (see dataset.json). */
  readonly nameNe: string;
  readonly wardCount: number;
  readonly hdxPcode: string;
  readonly inKathmanduValley: boolean;
  /** Lower-case ASCII letters of nameEn, for search (DATA_MODEL `name_norm`). */
  readonly nameNorm: string;
  /** Other published spellings (e.g. the census name when it differs). */
  readonly aliases: readonly string[];
};

export type ZoneKind = "valley" | "ring_road" | "custom_platform";
export type Zone = {
  readonly code: string;
  readonly name: string;
  readonly kind: ZoneKind;
  /** `inferred` = derived by us and pending owner review; `gap` = no membership data yet. */
  readonly status: string;
  readonly basis: string;
  readonly localLevelCodes: readonly string[];
};

export type CourierAreaConfidence = "exact" | "manual" | "unmapped";
export type CourierArea = {
  readonly courier: string;
  readonly areaCode: string;
  readonly areaName: string;
  readonly branch: string;
  readonly localLevelCode: string | null;
  readonly confidence: CourierAreaConfidence;
  readonly version: string;
};

export function normalizeName(s: string): string {
  return s
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
}

export const PROVINCES: readonly Province[] = PROVINCE_ROWS.map(
  ([code, number, nameEn, nameNe, hdxPcode]) => ({
    code,
    number,
    nameEn,
    nameNe,
    hdxPcode,
  }),
);

export const DISTRICTS: readonly District[] = DISTRICT_ROWS.map(
  ([code, provinceCode, nameEn, nameNe, hdxPcode, aliases]) => ({
    code,
    provinceCode,
    nameEn,
    nameNe,
    hdxPcode,
    aliases,
  }),
);

export const LOCAL_LEVELS: readonly LocalLevel[] = LOCAL_LEVEL_ROWS.map(
  ([code, districtCode, type, nameEn, nameNe, wardCount, hdxPcode, valley, aliases]) => {
    const t = LOCAL_LEVEL_TYPES[type];
    if (!t) throw new Error(`geo: bad type index for ${code}`);
    return {
      code,
      districtCode,
      type: t,
      nameEn,
      nameNe,
      wardCount,
      hdxPcode,
      inKathmanduValley: valley === 1,
      nameNorm: normalizeName(nameEn),
      aliases,
    };
  },
);

export const ZONES: readonly Zone[] = ZONE_ROWS.map(
  ([code, name, kind, status, basis, localLevelCodes]) => ({
    code,
    name,
    kind: kind as ZoneKind,
    status,
    basis,
    localLevelCodes,
  }),
);

/** Courier area → local level mapping (DATA_MODEL `geo_courier_areas`). Empty stub in Phase 4. */
export const COURIER_AREAS: readonly CourierArea[] = COURIER_AREA_ROWS.map(
  ([courier, areaCode, areaName, branch, localLevelCode, confidence, version]) => ({
    courier,
    areaCode,
    areaName,
    branch,
    localLevelCode: localLevelCode || null,
    confidence: confidence as CourierAreaConfidence,
    version,
  }),
);

const byCode = <T extends { code: string }>(xs: readonly T[]) =>
  new Map(xs.map((x) => [x.code, x]));
const provinceIndex = byCode(PROVINCES);
const districtIndex = byCode(DISTRICTS);
const localLevelIndex = byCode(LOCAL_LEVELS);

export const getProvince = (code: string): Province | undefined => provinceIndex.get(code);
export const getDistrict = (code: string): District | undefined => districtIndex.get(code);
export const getLocalLevel = (code: string): LocalLevel | undefined => localLevelIndex.get(code);

export const districtsOf = (provinceCode: string): District[] =>
  DISTRICTS.filter((d) => d.provinceCode === provinceCode);

export const localLevelsOf = (districtCode: string): LocalLevel[] =>
  LOCAL_LEVELS.filter((l) => l.districtCode === districtCode);

/** Ward numbers of a local level (1…wardCount); empty for an unknown code. */
export function wardsOf(localLevelCode: string): number[] {
  const l = getLocalLevel(localLevelCode);
  return l ? Array.from({ length: l.wardCount }, (_, i) => i + 1) : [];
}

export function isValidWard(localLevelCode: string, ward: number): boolean {
  const l = getLocalLevel(localLevelCode);
  return !!l && Number.isInteger(ward) && ward >= 1 && ward <= l.wardCount;
}

/** Province → district → local level chain for a local-level code. */
export function lineage(
  localLevelCode: string,
): { province: Province; district: District; localLevel: LocalLevel } | undefined {
  const localLevel = getLocalLevel(localLevelCode);
  const district = localLevel && getDistrict(localLevel.districtCode);
  const province = district && getProvince(district.provinceCode);
  return localLevel && district && province ? { province, district, localLevel } : undefined;
}

export const zonesOf = (localLevelCode: string): Zone[] =>
  ZONES.filter((z) => z.localLevelCodes.includes(localLevelCode));

/**
 * Search-any-level lookup for the address cascade: matches local levels by English name, alias or
 * Devanagari name (prefix first, then substring). Returns at most `limit` results.
 */
export function searchLocalLevels(query: string, limit = 10): LocalLevel[] {
  const q = normalizeName(query);
  const qNe = query.normalize("NFC").trim();
  if (!q && !qNe) return [];
  const score = (l: LocalLevel): number => {
    const names = [l.nameNorm, ...l.aliases.map(normalizeName)];
    if (q && names.some((n) => n.startsWith(q))) return 0;
    if (qNe && l.nameNe.startsWith(qNe)) return 0;
    if (q && names.some((n) => n.includes(q))) return 1;
    if (qNe && l.nameNe.includes(qNe)) return 1;
    return -1;
  };
  return LOCAL_LEVELS.map((l) => ({ l, s: score(l) }))
    .filter((x) => x.s >= 0)
    .sort((a, b) => a.s - b.s || a.l.nameEn.localeCompare(b.l.nameEn))
    .slice(0, limit)
    .map((x) => x.l);
}
